/** @format */

import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import {Box, Button, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {clsx} from 'clsx';

import {Trans, useTranslation} from 'react-i18next';

import {providerNewIconSrc} from 'helpers/imageHelpers';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import {OauthProvider as Provider} from 'graphql/generated/types';

import {GraphTokensFromQuery} from './Steps/WizardCreated/interfaces';

const PREFIX = 'TOKEN_STEP';

const classes = {
  sidebar: `${PREFIX}-sidebar`,
  stepButton: `${PREFIX}-stepButton`,
  stepButtonIcon: `${PREFIX}-stepButtonIcon`,
  stepWrapper: `${PREFIX}-stepWrapper`,
  stepHeader: `${PREFIX}-stepHeader`,
  stepDescription: `${PREFIX}-stepDescription`,
  nameInput: `${PREFIX}-nameInput`,
  adButtonWrapper: `${PREFIX}-adButtonWrapper`,
  adButton: `${PREFIX}-adButton`,
  adButtonActive: `${PREFIX}-adButtonActive`,
  adButtonIcon: `${PREFIX}-adButtonIcon`,
  projectCreatedWrapper: `${PREFIX}-projectCreatedWrapper`,
  projectCreatedIcon: `${PREFIX}-projectCreatedIcon`,
  subbaccountsWrapper: `${PREFIX}-subbaccountsWrapper`,
  subbaccountsHeader: `${PREFIX}-subbaccountsHeader`,
  subbaccountsEditButton: `${PREFIX}-subbaccountsEditButton`,
  subbaccountsEditButtonIcon: `${PREFIX}-subbaccountsEditButtonIcon`,
  subbaccountsList: `${PREFIX}-subbaccountsList`,
  subbaccountsListItem: `${PREFIX}-subbaccountsListItem`,
  removeTokenButton: `${PREFIX}-removeTokenButton`,
  reconnectButton: `${PREFIX}-reconnectButton`,
};

const StyledBox = styled(Box)(({theme}) => ({
  [`& .${classes.stepDescription}`]: {
    marginTop: theme.spacing(4),
    color: '#F1F1F1',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '20px',
  },
  [`& .${classes.nameInput}`]: {
    width: '468px',
  },
  [`& .${classes.projectCreatedWrapper}`]: {
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.projectCreatedIcon}`]: {
    marginRight: theme.spacing(2),
    color: '#749F23',
    fontSize: '20px',
  },
  [`& .${classes.adButtonWrapper}`]: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  [`& .${classes.adButton}`]: {
    padding: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    height: '53px',
    color: '#F1F1F1',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    background: '#202020',
    borderRadius: '0',
    '&:hover': {
      background: '#4D4D4D',
    },
    '&:first-of-type': {
      borderRadius: '8px 0 0 8px',
    },
    '&:last-of-type': {
      borderRadius: '0 8px 8px 0',
    },
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      borderRadius: '0 !important',
    },
  },
  [`& .${classes.adButtonActive}`]: {
    background: '#4D4D4D',
  },
  [`& .${classes.adButtonIcon}`]: {
    width: 21,
    height: 21,
    marginRight: theme.spacing(2),
  },
  [`& .${classes.subbaccountsWrapper}`]: {
    maxWidth: '468px',
    width: '100%',
    borderRadius: '8px',
    background: '#202020',
    overflow: 'hidden',
  },
  [`& .${classes.subbaccountsHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#2B2B2B',
    color: '#8B8B8B',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '18px',
    letterSpacing: '0.32px',
  },
  [`& .${classes.subbaccountsEditButton}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(4),
    color: '#8B8B8B',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
  },
  [`& .${classes.subbaccountsEditButtonIcon}`]: {
    marginRight: theme.spacing(2),
    color: '#8B8B8B',
    fontSize: '16px',
  },
  [`& .${classes.subbaccountsList}`]: {
    background: '#202020',
    padding: theme.spacing(4),
  },
  [`& .${classes.subbaccountsListItem}`]: {
    marginTop: theme.spacing(1),
    color: '#F1F1F1',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '20px',
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  [`& .${classes.removeTokenButton}`]: {
    padding: theme.spacing(5, 4),
    borderRadius: '8px',
    background: '#202020',
    color: '#8B8B8B',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
  },
  [`& .${classes.reconnectButton}`]: {
    padding: theme.spacing(2, 4),
    borderRadius: '8px',
    background: '#202020',
    color: theme.palette.semantic_dark?.good.main,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
}));

const InvalidTokenBadge = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  marginTop: theme.spacing(2),
  padding: theme.spacing(0.5, 1),
  borderRadius: '9px',
  backgroundColor: theme.palette.semantic_dark?.bad.main,
  color: theme.palette.semantic_dark?.bad.on,
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '14px',
  '& .material-symbols-outlined': {
    fontSize: '14px',
  },
}));

interface Props {
  token?: GraphTokensFromQuery[number] | null;
  isAnalyticsStep?: boolean;
  activeProvider?: Provider | null;
  onEditSubbaccounts: (v: Provider) => void;
  onRemoveToken: (v: Provider) => void;
  onAdCompanyClick?: (v: Provider | null) => void;
  onAdConnect: (v: Provider) => void;
}

const providers = ['YANDEX', 'VKONTAKTE', 'VKONTAKTE_ADS', 'GOOGLE'] as Provider[];

export interface Token {
  id: number;
  provider: Provider;
  accounts: {
    id: number;
    name: string;
  }[];
}

const TokenStep = ({
  token,
  isAnalyticsStep,
  onRemoveToken,
  onEditSubbaccounts,
  onAdCompanyClick,
  onAdConnect,
  activeProvider,
}: Props) => {
  const {t} = useTranslation();

  const providerName = (v: Provider) => t(`provider_names.${v}`);

  const renderPermissions = (provider: Provider) => {
    return (
      <Box>
        <Box sx={{mt: 12}}>
          <Typography className={classes.stepDescription}>
            <Trans>Подключите доступы</Trans>
          </Typography>
        </Box>
        <Box sx={{mt: 2}}>
          <Typography className={classes.stepDescription}>
            <Trans>
              Вы перейдете на страницу авторизации, где нужно будет ввести адрес почты, привязанный
              к рекламному кабинету, и пароль.
            </Trans>
          </Typography>
        </Box>

        <Box sx={{mt: 4}}>
          <PrimaryMediumButton onClick={() => onAdConnect(provider)}>
            <Trans>Подключить</Trans>
          </PrimaryMediumButton>
        </Box>
      </Box>
    );
  };

  const renderProvidersMenu = () => {
    return (
      <>
        <Typography className={classes.stepDescription}>
          <Trans>Выберите рекламную систему</Trans>
        </Typography>

        <Box className={classes.adButtonWrapper}>
          {providers.map((provider: Provider, index: number) => (
            <Button
              className={clsx(
                classes.adButton,
                activeProvider === provider && classes.adButtonActive,
              )}
              key={index}
              onClick={() => onAdCompanyClick?.(provider)}
            >
              <img
                alt='status'
                src={providerNewIconSrc(provider, 20) || ''}
                className={classes.adButtonIcon}
              />
              {providerName(provider)}
            </Button>
          ))}
          <Button
            className={clsx(classes.adButton, !activeProvider && classes.adButtonActive)}
            onClick={() => onAdCompanyClick?.(null)}
          >
            <Trans>Не добавлять</Trans>
          </Button>
        </Box>
      </>
    );
  };

  const renderConnectedProvider = () => {
    if (!token) return null;
    return (
      <>
        <Box sx={{mt: 12}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 12,
            }}
          >
            <img
              alt='status'
              src={providerNewIconSrc(token.provider, 20) || ''}
              className={classes.adButtonIcon}
            />
            <Typography>
              <Trans>{providerName(token.provider)} подключен</Trans>
            </Typography>
          </Box>

          {token.isValid === false && (
            <InvalidTokenBadge>
              <Box component='span' className='material-symbols-outlined'>
                help
              </Box>
              <Trans>Недействительный токен</Trans>
            </InvalidTokenBadge>
          )}

          <Box sx={{mt: 2}} className={classes.subbaccountsWrapper}>
            <Box className={classes.subbaccountsHeader}>
              <Box sx={{p: 4}}>
                <Typography>
                  <Trans>Выбранные суббаккаунты</Trans>
                </Typography>
              </Box>

              <Button
                className={classes.subbaccountsEditButton}
                onClick={() => onEditSubbaccounts(token.provider)}
              >
                <ModeEditOutlineOutlinedIcon className={classes.subbaccountsEditButtonIcon} />
                <Trans>Изменить</Trans>
              </Button>
            </Box>
            <Box className={classes.subbaccountsList}>
              {token.accounts.map(v => (
                <Box key={v.id} className={classes.subbaccountsListItem}>
                  {v.name}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{mt: 4, display: 'flex', flexDirection: 'column', gap: '1px'}}>
            {token.isValid === false && (
              <Button
                className={classes.reconnectButton}
                onClick={() => onAdConnect(token.provider)}
              >
                <Box component='span' className='material-symbols-outlined' sx={{fontSize: '16px'}}>
                  restart_alt
                </Box>
                <Trans>Переподключить</Trans>
              </Button>
            )}
            <Button
              className={classes.removeTokenButton}
              onClick={() => onRemoveToken(token.provider)}
            >
              <Trans>Отключить аккаунт</Trans>
            </Button>
          </Box>
        </Box>
      </>
    );
  };
  return (
    <StyledBox>
      {token ? (
        renderConnectedProvider()
      ) : (
        <>
          {renderProvidersMenu()}

          {activeProvider && !isAnalyticsStep && renderPermissions(activeProvider)}
        </>
      )}
    </StyledBox>
  );
};

export default TokenStep;
